import { useDoctorDaySchedule } from "@/hooks/useDoctorDaySchedule";
import { format } from "date-fns";
import { Calendar, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface DoctorDaySchedulePanelProps {
  doctorId?: string;
  date?: Date;
}

// Clinic color mapping for visual distinction
const clinicColors: Record<string, string> = {
  "CLN001": "bg-emerald-500",
  "CLN002": "bg-blue-500",
  "CLN003": "bg-purple-500",
  "CLN004": "bg-amber-500",
  "CLN005": "bg-rose-500",
};

const getClinicColor = (clinicName: string) => {
  // Try to match by clinic code in name
  const code = Object.keys(clinicColors).find(code => clinicName.includes(code));
  return code ? clinicColors[code] : "bg-slate-500";
};

export const DoctorDaySchedulePanel = ({ doctorId, date }: DoctorDaySchedulePanelProps) => {
  const { data: schedule, isLoading } = useDoctorDaySchedule(doctorId, date);

  if (!doctorId || !date) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Doctor's Schedule Today (All Clinics)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!schedule || schedule.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Doctor's Schedule Today (All Clinics)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No appointments scheduled for this doctor on {format(date, "MMM dd, yyyy")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Doctor's Schedule Today (All Clinics)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {schedule.map((appointment) => (
            <div
              key={appointment.appointment_id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-[120px]">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">
                  {format(new Date(appointment.start_time), "HH:mm")}–
                  {format(new Date(appointment.end_time), "HH:mm")}
                </span>
              </div>
              
              <Badge 
                className={`${getClinicColor(appointment.clinic_name)} text-white shrink-0`}
              >
                {appointment.clinic_name}
              </Badge>
              
              <span className="text-sm truncate">
                {appointment.patient_name}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
